import _ from 'lodash';
import axios from '../requester';
import { API } from '../constants/api-urls';
import QueryString from 'query-string';
import React from 'react';
import Avatar from '@material-ui/core/Avatar';
import { Flex } from 'components/base/layout-components';
import { SPACES } from 'common/constants/sizes';
import Text from 'components/base/Text';
import { SingleTeamLabelWrapper } from 'components/ItemPicker';
import styled from 'styled-components';
import { getColor } from 'common/constants/_colors';
import BrowserStorageAccessor from '../utils/BrowserStorageAccessor';
import TextWithTooltipOnEllipsis from 'components/base/TextWithTooltipOnEllipsis';
import {
  LOCAL_STORAGE_KEYS,
  LOCAL_STORAGE_SELECTED_TEAM_ID_NO_ORG,
  SESSION_STORAGE_KEYS
} from '../constants/constants';
import { getAllTeamsGroup } from 'common/utils/utils';

const localStorageAccessor = BrowserStorageAccessor().local;
const sessionStorageAccessor = BrowserStorageAccessor().session;

export const TEAMS_DEFAULT_ORDER_BY = { keys: ['name'], dir: ['asc'] };

export const removeStoredTeamIfNotExistsAndSelectTeamIfNeeded = (
  teams,
  selectTeamId,
  shouldResolveTeamId,
  treeTeams
) => {
  const allTeamsGroup = getAllTeamsGroup(treeTeams);
  let storedTeamId = localStorageAccessor.get(
    LOCAL_STORAGE_KEYS.SELECTED_TEAM_ID
  );

  if (!storedTeamId) {
    storedTeamId = localStorage.getItem(LOCAL_STORAGE_SELECTED_TEAM_ID_NO_ORG);
  }

  if (!teams.find(({ id }) => id === parseInt(storedTeamId))) {
    if (shouldResolveTeamId) {
      const orderedTeams = _.orderBy(treeTeams, item =>
        item?.name?.toLowerCase()
      );
      const teamId = _.get(orderedTeams, '[0].id', '');

      if (teamId) {
        selectTeamId(teamId);
        localStorageAccessor.set(LOCAL_STORAGE_KEYS.SELECTED_TEAM_ID, teamId);
        localStorage.setItem(LOCAL_STORAGE_SELECTED_TEAM_ID_NO_ORG, teamId);
      }
    } else if (!_.isEmpty(allTeamsGroup)) {
      selectTeamId(allTeamsGroup.id);
      localStorageAccessor.set(
        LOCAL_STORAGE_KEYS.SELECTED_TEAM_ID,
        allTeamsGroup.id
      );
      localStorage.setItem(
        LOCAL_STORAGE_SELECTED_TEAM_ID_NO_ORG,
        allTeamsGroup.id
      );
    } else {
      // TO DO: when we delete the old all teams after GA of all teams group, need to delete this:
      selectTeamId();
      localStorageAccessor.remove(LOCAL_STORAGE_KEYS.SELECTED_TEAM_ID);
      localStorage.removeItem(LOCAL_STORAGE_SELECTED_TEAM_ID_NO_ORG);
    }
  }
};

export const getTeams = async ({
  from,
  to,
  active_members_only,
  nonmerged_members_only,
  lean_team
}) => {
  const params = {
    add_excluded_contributors: false,
    sensor_type_id: 1,
    ...(to ? { before: to } : {}),
    ...(from ? { after: from } : {}),
    ...(active_members_only ? { active_members_only: true } : {}),
    ...(nonmerged_members_only ? { nonmerged_members_only: true } : {}),
    ...(lean_team ? { lean_team: true } : {})
  };

  return axios.get(API.TEAMS_PATH, { params });
};
export const createTeam = async body => axios.post(API.TEAMS_PATH, body);
export const updateTeam = async body =>
  axios.patch(`${API.TEAMS_PATH}/${body.id}`, body);
export const updateTeamContributors = async ({
  contributors_to_add,
  contributors_ids_to_remove,
  hard_delete,
  with_history,
  id,
  backfill_contributors_only,
  isGroup
}) => {
  return await axios.post(`${API.TEAMS_PATH}/${id}/contributors`, {
    with_history,
    contributors_to_add,
    contributors_ids_to_remove,
    ...(isGroup ? { backfill_contributors_only } : {}),
    ...(hard_delete ? { hard_delete: true } : {})
  });
};
export const deleteTeam = async id => axios.delete(`${API.TEAMS_PATH}/${id}`);

const ClickableSingleTeamLabel = styled(Flex)`
  width: 20rem;
  height: 4.5rem;
  cursor: pointer;
  padding-left: 1rem;
  margin-right: 1rem;
  border: 1px solid transparent;
  :hover {
    border-radius: 3px;
    border: 1px solid ${getColor('botticelli')};
    background-color: ${getColor('zircon')};
  }
`;

export const matchTeamMetricsToTeam = (teams, metrics) => {
  const teamById = _.keyBy(teams, 'id');

  return metrics
    .map(metric => {
      const { team_id } = metric;
      const team = teamById[team_id];

      if (!team) {
        // eslint-disable-next-line no-console
        console.log(
          `Team with id ${team_id} does not returned from server, but metrics with this team id
          has arrived.`
        );
        return null;
      }

      return {
        ...team,
        ...metric
      };
    })
    .filter(Boolean);
};

export function handleTeamIdOnUrl(team, history, isTeamLicense) {
  const teamId = team?.id;
  const search = QueryString.parse(window.location.search, {
    ignoreQueryPrefix: true
  });

  const searchObject = teamId
    ? Object.assign({}, search, { teams: teamId })
    : _.omit(search, ['teams']);
  const newSearchString = `?${QueryString.stringify(searchObject)}`;
  history.replace({
    pathname: window.location.pathname,
    search: newSearchString
  });
}

export function onTeamSelection({
  team,
  reduxActionFn,
  history,
  isTeamLicense
}) {
  const teamId = team?.id;
  if (teamId > 0) {
    reduxActionFn(teamId);
    localStorageAccessor.set(LOCAL_STORAGE_KEYS.SELECTED_TEAM_ID, teamId);
    localStorage.setItem(LOCAL_STORAGE_SELECTED_TEAM_ID_NO_ORG, teamId);
    handleTeamIdOnUrl(team, history, isTeamLicense);
  } else {
    reduxActionFn();
    handleTeamIdOnUrl(null, history, isTeamLicense);
    localStorageAccessor.remove(LOCAL_STORAGE_KEYS.SELECTED_TEAM_ID, teamId);
    localStorage.removeItem(LOCAL_STORAGE_SELECTED_TEAM_ID_NO_ORG);
  }
  sessionStorageAccessor.remove(SESSION_STORAGE_KEYS.METRICS_FILTERS);
}

const SingleTeamLabel = ({
  color,
  initials,
  name,
  weight,
  onClick,
  isClickable,
  withEllipsis
}) => {
  const content = [
    /* eslint-disable react/jsx-key */
    <Avatar style={{ backgroundColor: color }}>
      <Text weight='bold' color='white'>
        {initials}
      </Text>
    </Avatar>,
    <TextWithTooltipOnEllipsis
      pl={SPACES.small}
      size='large'
      color='shark'
      data-cy={name}
      ellipsisAfter={withEllipsis ? (isClickable ? '14rem' : '10.5rem') : null}
      weight={weight}
    >
      {name}
    </TextWithTooltipOnEllipsis>
    /* eslint-enable react/jsx-key */
  ];

  return isClickable ? (
    <ClickableSingleTeamLabel items='center' onClick={onClick}>
      {content.map((item, idx) => (
        <React.Fragment key={idx}>{item}</React.Fragment>
      ))}
    </ClickableSingleTeamLabel>
  ) : (
    <Flex items='center'>
      {content.map((item, idx) => (
        <React.Fragment key={idx}>{item}</React.Fragment>
      ))}
    </Flex>
  );
};

export const getTeamPicker = ({
  selectedTeam,
  isClickable = true,
  onSingleModeClick
}) => {
  return (
    <SingleTeamLabelWrapper>
      <SingleTeamLabel
        {...selectedTeam}
        isClickable={isClickable}
        withEllipsis
        onClick={onSingleModeClick}
      />
    </SingleTeamLabelWrapper>
  );
};
